import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import React, { FunctionComponent, memo, useCallback, useMemo } from 'react'

import { payoutPriceRenderer } from '../../utils/buybackPayoutHelpersNew'
import { BuybackFormPayoutBalanceTableSummary } from '../BuybackFormPayoutBalanceTableSummary/BuybackFormPayoutBalanceTableSummary'
import {
  BuybackFormPayoutOutcomeTable,
  BuybackPayoutDebtData,
  BuybackPayoutProcessCostData,
} from '../BuybackFormPayoutOutcomeTable/BuybackFormPayoutOutcomeTable'

export interface BuybackFormPayoutBalanceTableProps {
  offerPrice: number
  totalOutcome: number
  requested: number
  propertyPrice: number
  debts: BuybackPayoutDebtData[]
  processCosts: BuybackPayoutProcessCostData[]
  commission?: number
  baseCommission: number
  debtFeeTotal?: number
  baseDebtFeeTotal: number
  debtAmountTotal: number
  processCostTotal: number
}

interface RowData {
  key: string
  category: string
  description: string | React.ReactNode
  amount: number | string
}

const balanceColumns: ColumnsType<RowData> = [
  {
    title: 'Položka',
    dataIndex: 'category',
    key: 'category',
    width: '25%',
  },
  Table.EXPAND_COLUMN,
  {
    title: 'Popis',
    dataIndex: 'description',
    key: 'description',
    className: 'group-hover:text-primary',
  },
  {
    title: 'Částka',
    dataIndex: 'amount',
    key: 'amount',
    width: 100,
    className: '!text-right whitespace-nowrap',
    render: payoutPriceRenderer,
  },
]

const rowClassName = (record: RowData) =>
  record.key === 'outcome' ? 'group cursor-pointer transition-colors' : ''

export const BuybackFormPayoutBalanceTable: FunctionComponent<BuybackFormPayoutBalanceTableProps> =
  memo(
    ({
      offerPrice,
      totalOutcome,
      requested,
      propertyPrice,
      debts,
      processCosts,
      commission,
      baseCommission,
      debtFeeTotal,
      baseDebtFeeTotal,
      debtAmountTotal,
      processCostTotal,
    }: BuybackFormPayoutBalanceTableProps) => {
      const dataSource = useMemo<RowData[]>(
        () => [
          {
            key: 'propertyPrice',
            category: 'Cenový odhad',
            description: 'Výchozí tržní cena nemovitosti',
            amount: propertyPrice,
          },
          {
            key: 'buyback',
            category: 'Výkupní nabídka',
            description: 'Cenová nabídka výkupu nemovitosti',
            amount: offerPrice,
          },
          {
            key: 'outcome',
            category: 'Celkové náklady',
            description: 'Závazky + odměna + poplatky + provize',
            amount: totalOutcome,
          },
          {
            key: 'wanted',
            category: 'Potřeby klienta',
            description: 'Částka požadovaná klientem nad rámec závazků',
            amount: requested,
          },
        ],
        [propertyPrice, offerPrice, totalOutcome, requested]
      )

      const summary = useCallback(
        () => (
          <BuybackFormPayoutBalanceTableSummary
            offerPrice={offerPrice}
            totalOutcome={totalOutcome}
            requested={requested}
          />
        ),
        [offerPrice, totalOutcome, requested]
      )

      return (
        <Table<RowData>
          size="small"
          className="border-t-2 border-gray-100 my-12"
          pagination={false}
          dataSource={dataSource}
          columns={balanceColumns}
          summary={summary}
          rowClassName={rowClassName}
          expandable={{
            rowExpandable: record => record.key === 'outcome',
            showExpandColumn: true,
            defaultExpandAllRows: true,
            expandRowByClick: true,
            expandedRowRender: () => (
              <BuybackFormPayoutOutcomeTable
                debts={debts}
                processCosts={processCosts}
                commission={commission}
                baseCommission={baseCommission}
                debtFeeTotal={debtFeeTotal}
                baseDebtFeeTotal={baseDebtFeeTotal}
                debtAmountTotal={debtAmountTotal}
                processCostTotal={processCostTotal}
              />
            ),
          }}
        />
      )
    }
  )
